import { useState } from 'react'
import {
  type DisassemblyStep,
  type EndOfLifeData,
} from '@esf/shared'

interface EndOfLifeFormProps {
  endOfLife: EndOfLifeData | null
  onChange: (endOfLife: EndOfLifeData) => void
  disabled?: boolean
}

const emptyStep: DisassemblyStep = {
  stepNumber: 1,
  instruction: '',
  toolsRequired: [],
  estimatedTimeMinutes: 5,
  safetyWarnings: '',
}

const defaultEndOfLife: EndOfLifeData = {
  disassemblyInstructions: [],
  recyclableMaterialsPercent: 0,
  hazardousWastePercent: 0,
  recyclingFacilityRequirements: '',
  disposalInstructions: '',
  takeback: undefined,
}

export default function EndOfLifeForm({
  endOfLife,
  onChange,
  disabled,
}: EndOfLifeFormProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [toolsInput, setToolsInput] = useState<Record<number, string>>({})

  const data = endOfLife || defaultEndOfLife

  const updateField = <K extends keyof EndOfLifeData>(
    field: K,
    value: EndOfLifeData[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const addStep = () => {
    const nextNumber = data.disassemblyInstructions.length + 1
    const newSteps = [...data.disassemblyInstructions, { ...emptyStep, stepNumber: nextNumber }]
    onChange({ ...data, disassemblyInstructions: newSteps })
    setExpandedIndex(newSteps.length - 1)
  }

  const removeStep = (index: number) => {
    const newSteps = data.disassemblyInstructions
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, stepNumber: i + 1 }))
    onChange({ ...data, disassemblyInstructions: newSteps })
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
  }

  const updateStep = (index: number, field: keyof DisassemblyStep, value: string | number | string[]) => {
    const newSteps = data.disassemblyInstructions.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    )
    onChange({ ...data, disassemblyInstructions: newSteps })
  }

  const handleToolsChange = (index: number, value: string) => {
    setToolsInput(prev => ({ ...prev, [index]: value }))
    const tools = value.split(',').map(s => s.trim()).filter(Boolean)
    updateStep(index, 'toolsRequired', tools)
  }

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= data.disassemblyInstructions.length) return

    const newSteps = [...data.disassemblyInstructions]
    const temp = newSteps[index]
    newSteps[index] = newSteps[newIndex]
    newSteps[newIndex] = temp

    // Renumber steps
    newSteps.forEach((step, i) => {
      step.stepNumber = i + 1
    })

    onChange({ ...data, disassemblyInstructions: newSteps })
    setExpandedIndex(newIndex)
  }

  const totalTime = data.disassemblyInstructions.reduce(
    (sum, step) => sum + (step.estimatedTimeMinutes || 0),
    0
  )

  return (
    <div className="space-y-6">
      {/* Recyclability Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-xs text-green-600 mb-1">Recyclable Materials</p>
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              value={data.recyclableMaterialsPercent}
              onChange={e => updateField('recyclableMaterialsPercent', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="w-16 text-lg font-bold text-green-900 bg-transparent border-none p-0 focus:ring-0"
              min="0"
              max="100"
            />
            <span className="text-green-700">%</span>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs text-amber-600 mb-1">Hazardous Waste</p>
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              value={data.hazardousWastePercent || 0}
              onChange={e => updateField('hazardousWastePercent', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="w-16 text-lg font-bold text-amber-900 bg-transparent border-none p-0 focus:ring-0"
              min="0"
              max="100"
            />
            <span className="text-amber-700">%</span>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs text-blue-600 mb-1">Est. Disassembly Time</p>
          <p className="text-lg font-bold text-blue-900">{totalTime} min</p>
        </div>
      </div>

      {/* Disassembly Instructions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-medium text-gray-700">Disassembly Instructions</h4>
            <p className="text-xs text-gray-500">Step-by-step non-destructive disassembly guide</p>
          </div>
          <button
            type="button"
            onClick={addStep}
            disabled={disabled}
            className="btn btn-secondary text-sm py-1 px-3"
          >
            + Add Step
          </button>
        </div>

        {data.disassemblyInstructions.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No disassembly instructions</p>
            <p className="text-xs text-gray-400">Add steps for end-of-life processing</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.disassemblyInstructions.map((step, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Step Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-esf-navy text-white text-xs font-bold">
                      {step.stepNumber}
                    </span>
                    <span className="font-medium text-sm truncate max-w-[300px]">
                      {step.instruction || 'Empty step'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {step.estimatedTimeMinutes > 0 && (
                      <span className="text-xs text-gray-500">{step.estimatedTimeMinutes} min</span>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); moveStep(index, 'up'); }}
                        disabled={disabled || index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); moveStep(index, 'down'); }}
                        disabled={disabled || index === data.disassemblyInstructions.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    <svg
                      className={`h-5 w-5 text-gray-400 transition-transform ${
                        expandedIndex === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedIndex === index && (
                  <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Instruction <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={step.instruction}
                        onChange={e => updateStep(index, 'instruction', e.target.value)}
                        disabled={disabled}
                        className="filter-input w-full min-h-[80px] resize-y"
                        rows={3}
                        placeholder="Describe this disassembly step..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tools Required</label>
                        <input
                          type="text"
                          value={toolsInput[index] ?? (step.toolsRequired || []).join(', ')}
                          onChange={e => handleToolsChange(index, e.target.value)}
                          disabled={disabled}
                          className="filter-input w-full"
                          placeholder="Phillips screwdriver, 10mm wrench"
                        />
                        {(step.toolsRequired?.length || 0) > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {step.toolsRequired?.map((tool, i) => (
                              <span key={i} className="badge bg-gray-100 text-gray-800">{tool}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (minutes)</label>
                        <input
                          type="number"
                          value={step.estimatedTimeMinutes}
                          onChange={e => updateStep(index, 'estimatedTimeMinutes', parseInt(e.target.value) || 0)}
                          disabled={disabled}
                          className="filter-input w-full"
                          min="1"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Safety Warnings</label>
                      <input
                        type="text"
                        value={step.safetyWarnings || ''}
                        onChange={e => updateStep(index, 'safetyWarnings', e.target.value)}
                        disabled={disabled}
                        className="filter-input w-full"
                        placeholder="e.g., Wear safety glasses, sharp edges"
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => removeStep(index)}
                        disabled={disabled}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Remove Step
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recycling Facility Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Recycling Facility Requirements</label>
        <textarea
          value={data.recyclingFacilityRequirements || ''}
          onChange={e => updateField('recyclingFacilityRequirements', e.target.value)}
          disabled={disabled}
          className="filter-input w-full min-h-[80px] resize-y"
          rows={3}
          placeholder="Specify required certifications or capabilities for recycling facilities..."
        />
      </div>

      {/* Disposal Instructions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">General Disposal Instructions</label>
        <textarea
          value={data.disposalInstructions || ''}
          onChange={e => updateField('disposalInstructions', e.target.value)}
          disabled={disabled}
          className="filter-input w-full min-h-[80px] resize-y"
          rows={3}
          placeholder="Provide end-of-life disposal guidance for consumers and recyclers..."
        />
      </div>

      {/* Takeback Program */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            id="takeback"
            checked={data.takeback !== undefined}
            onChange={e => updateField('takeback', e.target.checked ? { available: true, contact: '' } : undefined)}
            disabled={disabled}
            className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label htmlFor="takeback" className="text-sm font-medium text-purple-800">
            Takeback program available
          </label>
        </div>

        {data.takeback && (
          <div className="ml-7 space-y-3">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Contact Information</label>
              <input
                type="text"
                value={data.takeback.contact || ''}
                onChange={e => updateField('takeback', { ...data.takeback!, contact: e.target.value })}
                disabled={disabled}
                className="filter-input w-full"
                placeholder="Email or phone for takeback requests"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">Program URL</label>
              <input
                type="url"
                value={data.takeback.url || ''}
                onChange={e => updateField('takeback', { ...data.takeback!, url: e.target.value })}
                disabled={disabled}
                className="filter-input w-full"
                placeholder="https://example.com/takeback"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
